import { useState, useEffect, createContext, useContext } from 'react';
import api from '../services/api';

// Use app-specific keys so student and admin tokens don't overwrite each other when on same domain
const TOKEN_KEY = 'token_student';
const USER_KEY = 'user_student';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip token verification during react-snap prerendering (no backend)
    if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') {
      setLoading(false);
      return;
    }
    let token = localStorage.getItem(TOKEN_KEY);
    let savedUser = localStorage.getItem(USER_KEY);

    // Migrate legacy keys (token/user) to student-specific keys for existing sessions
    if (!token && !savedUser) {
      const legacyToken = localStorage.getItem('token');
      const legacyUser = localStorage.getItem('user');
      if (legacyToken && legacyUser) {
        try {
          const parsed = JSON.parse(legacyUser);
          if (parsed?.role === 'student') {
            localStorage.setItem(TOKEN_KEY, legacyToken);
            localStorage.setItem(USER_KEY, legacyUser);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            token = legacyToken;
            savedUser = legacyUser;
          }
        } catch {
          /* ignore */
        }
      }
    }

    if (token && savedUser) {
      let parsedUser = null;
      try {
        parsedUser = JSON.parse(savedUser);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setLoading(false);
      return;
    }
      if (typeof token !== 'string' || !parsedUser || typeof parsedUser !== 'object') {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setLoading(false);
        return;
      }
      setUser(parsedUser);
      // Verify token is still valid
      api.get('/auth/profile')
        .then((res) => {
          setUser(res.data.user);
        })
        .catch(() => {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const trimmedEmail = typeof email === 'string' ? email.trim() : '';
    const trimmedPassword = typeof password === 'string' ? password : '';
    if (!trimmedEmail || !trimmedPassword) {
      const err = new Error('Email and password are required');
      err.response = { data: { error: 'Email and password are required' } };
      throw err;
    }
    const res = await api.post('/auth/login', { email: trimmedEmail, password: trimmedPassword });
    const token = res.data?.token;
    const userData = res.data?.user;
    if (typeof token === 'string' && token && userData) {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
    }
    setUser(userData || null);
    // Reconnect sockets with new token
    if (window.reconnectSockets) {
      window.reconnectSockets();
    }
    return res.data;
  };

  const register = async (userData) => {
    const res = await api.post('/auth/register', userData);
    const token = res.data?.token;
    const userFromRes = res.data?.user;
    if (typeof token === 'string' && token && userFromRes) {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(userFromRes));
    }
    setUser(userFromRes || null);
    // Reconnect sockets with new token
    if (window.reconnectSockets) {
      window.reconnectSockets();
    }
    return res.data;
  };

  const googleAuth = async (token) => {
    if (typeof token !== 'string' || !token) {
      const err = new Error('Invalid Google credential');
      err.response = { data: { error: 'Invalid Google credential' } };
      throw err;
    }
    const res = await api.post('/auth/google', { token });
    const authToken = res.data?.token;
    const userFromRes = res.data?.user;
    if (typeof authToken === 'string' && authToken && userFromRes) {
      localStorage.setItem(TOKEN_KEY, authToken);
      localStorage.setItem(USER_KEY, JSON.stringify(userFromRes));
    }
    setUser(userFromRes || null);
    // Reconnect sockets with new token
    if (window.reconnectSockets) {
      window.reconnectSockets();
    }
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    // Disconnect sockets on logout
    if (window.disconnectAllSockets) {
      window.disconnectAllSockets();
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, googleAuth, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

